import { Box, CSSProp, styled, Theme, css, StyleSystemProps } from '@withjoy/joykit';
import { animationTransition } from '@shared/utils/animationTransition';
import type { ButtonVariant, SharedButtonStyleProps } from './Button.types';

const getBorderRadiiStyles = ({ $shape }: SharedButtonStyleProps) => {
  let value: string = '4px';
  if ($shape === 'pill') {
    value = '100px';
  } else if ($shape === 'circle') {
    value = '48px';
  }

  return {
    borderRadius: value
  };
};

const paddingStylesBySize = {
  default: {
    paddingLeft: '1.5rem',
    paddingRight: '1.5rem',
    paddingTop: '0.75rem',
    paddingBottom: '0.75rem'
  },
  mini: {
    paddingLeft: '2rem',
    paddingRight: '2rem',
    paddingTop: '0.5rem',
    paddingBottom: '0.5rem'
  }
};

export const getPaddingStyles = ({ $variant, $size, $shape }: SharedButtonStyleProps) => {
  if ($variant === 'link') {
    return { padding: 3 };
  } else if ($shape === 'circle') {
    return $size === 'default'
      ? {
          padding: '0.75rem'
        }
      : {
          padding: '0.5rem'
        };
  } else {
    return paddingStylesBySize[$size];
  }
};

// ==============================

type ButtonDisabledStyles = Readonly<
  Partial<{
    color: StyleSystemProps['color'];
    backgroundColor: StyleSystemProps['color'];
    border: string;
    borderColor?: StyleSystemProps['color'];
  }>
>;

const disabledStylesByVariant: ReadonlyRecord<ButtonVariant, ButtonDisabledStyles> = {
  solid: {
    backgroundColor: 'mono3',
    color: 'mono6'
  },
  outline: {
    backgroundColor: 'mono2',
    border: `1px solid`,
    borderColor: 'mono6',
    color: 'mono6'
  },
  ghost: {
    color: 'mono6'
  },
  link: {
    color: 'mono6'
  }
};

type ConfigurableColorProperties = Partial<{
  color: StyleSystemProps['color'];
  backgroundColor: StyleSystemProps['color'];
  border: string;
  borderColor?: StyleSystemProps['color'];
}>;

type ButtonColorStyles = Readonly<
  Partial<
    ConfigurableColorProperties & {
      _hover: ConfigurableColorProperties;
      _active: ConfigurableColorProperties;
    }
  >
>;

const colorStylesByVariant: ReadonlyRecord<ButtonVariant, ButtonColorStyles> = {
  solid: {
    color: 'white',
    backgroundColor: 'fillDark',
    _hover: {
      backgroundColor: 'fillDarkHover'
    },
    _active: {
      backgroundColor: 'fillDarkActive'
    }
  },
  outline: {
    color: 'primary7',
    backgroundColor: 'white',
    border: `1px solid`,
    borderColor: 'stroke',
    _hover: {
      backgroundColor: 'mono1'
    },
    _active: {
      backgroundColor: 'mono2'
    }
  },
  ghost: {
    color: 'primary7',
    backgroundColor: 'transparent',
    _hover: {
      backgroundColor: 'primary9'
    },
    _active: {
      backgroundColor: 'fillLightActive'
    }
  },
  link: {
    color: 'mono14',
    _hover: {
      color: 'linkHover'
    },
    _active: {
      color: 'linkActive'
    }
  }
};

const getVariantStyles = ({ $colorScheme, $isDisabled, $variant }: SharedButtonStyleProps): ButtonColorStyles | undefined => {
  return $isDisabled ? disabledStylesByVariant[$variant] : colorStylesByVariant[$variant];
};

export const getButtonStyles = (sharedStyleProps: SharedButtonStyleProps): {} => {
  const rules: StyleSystemProps = {
    transition: animationTransition('color', 'background-color', 'border', 'box-shadow'),
    _enabled: {
      _focus: {
        boxShadow: `0px 0px 0px 2px`,
        color: 'primary7'
      }
    },
    ...getPaddingStyles(sharedStyleProps),
    ...getBorderRadiiStyles(sharedStyleProps),
    ...getVariantStyles(sharedStyleProps)
  };

  return rules;
};

export const StyledBaseButton = styled(Box)<{ $css?: CSSProp<Theme> }>``;

const truncateMixin = css`
  display: inline-block;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

export const StyledButtonLabel = styled.span<{ $truncate: boolean }>`
  display: inherit;
  justify-content: inherit;
  align-items: inherit;
  transition: ${animationTransition('color')};
  ${({ $truncate }) => ($truncate ? truncateMixin : undefined)};
`;

export const StyledButtonIcon = styled(Box).attrs({ as: 'span' })`
  display: inherit;
  & path {
    transition: ${animationTransition('fill')};
  }
`;
